"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ShiftHistory = void 0;
const ui_1 = require("@app/component/ui");
const table_config_1 = require("@app/component/ui/table/table-config");
const employees_1 = require("@app/request/employees");
const load_shift_history_1 = require("@app/request/load-shift-history");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const diff_1 = require("@app/util/diff");
const useWorkspace_1 = require("@app/util/hooks/useWorkspace");
const sentry_error_1 = require("@app/util/sentry/sentry-error");
const shift_diff_parser_1 = require("@app/util/shift-diff-parser");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const shift_context_1 = require("../shift-context");
const ShiftHistory = () => {
    const { initialData } = (0, shift_context_1.useShift)();
    const shiftId = initialData?.shiftId;
    const workspaceId = (0, store_1.useRootSelector)((state) => state.workspaceId);
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const [events, setEvents] = react_1.default.useState([]);
    const contractTypes = workspace?.contractTypes?.filter(util_1.isValue);
    const [employees, setEmployees] = react_1.default.useState([]);
    const contracts = react_1.default.useMemo(() => employees.flatMap((emp) => emp.contracts), [employees]);
    const positions = (0, store_1.useRootSelector)((state) => state.positions);
    const diffParser = Object.create((0, shift_diff_parser_1.SHIFT_DIFF_PARSER)({
        employees,
        contractTypes,
        contracts,
        positions,
    }));
    if (!(0, util_1.isValue)(workspaceId)) {
        throw new sentry_error_1.SentryError('AuditLog: workspaceId cannot be null or undefined');
    }
    const loadData = react_1.default.useCallback(async () => {
        try {
            const members = await (0, employees_1.requestEmployeesV2)({
                id: workspaceId,
            });
            const loadedEmployees = Array.isArray(members)
                ? members
                    .filter((m) => Array.isArray(m.roles) && m.roles.length > 0)
                    .filter((m) => m.roles?.some((r) => (0, util_1.isValue)(r) && !r.hidden))
                    .map((m) => {
                    const firstRole = m.roles?.find((r) => (0, util_1.isValue)(r));
                    if (!(0, util_1.isValue)(m.user) || !(0, util_1.isValue)(firstRole))
                        return null;
                    return util_1.miscUtil.getEmployeeObjectForStore(m.user, firstRole);
                })
                    .filter(util_1.isValue)
                : [];
            setEmployees(loadedEmployees);
        }
        catch (e) {
            setEmployees([]);
        }
    }, [workspaceId]);
    react_1.default.useEffect(() => {
        loadData();
    }, []);
    const tableConfig = (0, table_config_1.useMemoizedNullableTableConfig)([
        {
            key: 'who',
            tlKey: 'WHO',
            minWidth: 175,
            width: 175,
            maxWidth: 175,
            rowToHTML: (row) => {
                if (row.author === 'admin') {
                    return (0, util_1.t)('SYSTEM');
                }
                const employee = employees.find((emp) => emp.id === row.author);
                return employee?.name ?? '-';
            },
            rowToStr: (row) => {
                if (row.author === 'admin') {
                    return (0, util_1.t)('SYSTEM');
                }
                const employee = employees.find((emp) => emp.id === row.author);
                return employee?.name ?? '-';
            },
        },
        {
            key: 'when',
            tlKey: 'WHEN',
            minWidth: 150,
            width: 150,
            maxWidth: 150,
            rowToHTML: (row) => (0, moment_1.default)(row.created).format('DD.MM HH:mm'),
            rowToStr: (row) => (0, moment_1.default)(row.created).format('DD.MM HH:mm'),
        },
        {
            key: 'changes',
            tlKey: 'CHANGES',
            minWidth: 150,
            width: 150,
            maxWidth: 150,
            rowToHTML: (row) => (0, util_1.t)(`audit.shift.${row.type ?? ''}`),
            rowToStr: (row) => (0, util_1.t)(`audit.shift.${row.type ?? ''}`),
        },
        {
            key: 'description',
            tlKey: 'DESCRIPTION',
            minWidth: 300,
            width: 400,
            maxWidth: 500,
            rowToHTML: (row) => {
                const _diffs = row.diff;
                if (!(0, util_1.isValue)(_diffs)) {
                    return '-';
                }
                const parsedDiffs = (0, diff_1.parseDiff)(_diffs)
                    .filter((diff) => (0, util_1.isValue)(diff.oldValue) || (0, util_1.isValue)(diff.newValue))
                    .filter((diff) => !diff.path.includes('id'));
                const stringifyValue = (t) => moment_1.default.utc(t, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]', true).isValid()
                    ? moment_1.default
                        .utc(t, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]', true)
                        .format('DD. MM. YYYY')
                    : (t ?? '[-]');
                return (react_1.default.createElement("ul", null, parsedDiffs.map((change, index) => {
                    const valueString = `${(0, util_1.t)('FROM')} ${stringifyValue(change.oldValue)} ${(0, util_1.t)('TO')} ${stringifyValue(change.newValue)}`;
                    const parsedValue = change.path.split('.').reduce((acc, curr) => {
                        if (!acc || typeof acc === 'function')
                            return null;
                        const key = acc[curr];
                        if (typeof key === 'function') {
                            return key(change.type, change.oldValue, change.newValue);
                        }
                        if (key && typeof key === 'object') {
                            return key;
                        }
                        return null;
                    }, diffParser);
                    const label = (0, util_1.isValue)(parsedValue) ? (parsedValue) : (react_1.default.createElement(react_1.default.Fragment, null,
                        change.path,
                        " ",
                        valueString));
                    return (react_1.default.createElement("li", { key: `diff-${change.path}-${index}` },
                        (0, util_1.t)(change.type),
                        ": ",
                        label));
                })));
            },
            rowToStr: () => null,
        },
    ], [employees.length]);
    react_1.default.useEffect(() => {
        if ((0, util_1.isUndefined)(shiftId))
            return;
        const action = async () => {
            const _events = await (0, load_shift_history_1.loadShiftHistory)({ id: shiftId });
            if ((0, util_1.isUndefined)(_events))
                return;
            setEvents(_events);
        };
        void action();
    }, []);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(ui_1.Text, { type: 'body-large', weight: 'bold' }, (0, util_1.t)('HISTORY')),
        react_1.default.createElement(ui_1.Spacing, { type: ui_1.Spacing.TYPES.VERTICAL, size: ui_1.Spacing.SIZES.SIZE_16 }),
        react_1.default.createElement("div", { style: { display: 'flex', height: 'calc(100% - 3rem)' } },
            react_1.default.createElement(ui_1.Table, { size: ui_1.Table.SIZE.SLIM, type: ui_1.Table.TYPES.FILL, tableName: 'shift-history', rows: events, tableConfig: tableConfig, pagination: false }))));
};
exports.ShiftHistory = ShiftHistory;
